import { computed, readonly, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { FencyMood } from "../shared/fency/fency";

/**
 * Fency, the companion who lives in the dashboard shell (FencyCompanion.vue).
 *
 * One shared state for the whole app, so any page can make Fency speak or
 * react and every placement (sidebar dock, rail, mobile header) shows the
 * same Fency:
 * - `say()` puts a line in Fency's speech bubble. Lines have a priority: a
 *   page hint never talks over a greeting or a reaction.
 * - `react()` plays a reaction (hearts, sparkles, a startle) without words,
 *   e.g. when a success or error toast appears.
 * - The bond: Fency remembers each user (per browser), how long they have
 *   known each other and when they last met, greets them accordingly and
 *   celebrates milestones together.
 * - Nobody around for a while: Fency dozes off, and wakes up with a start.
 * - One Fency per screen: when a page shows Fency itself (a chat, a lesson,
 *   the home summary, an AI dialog, an illustration), it claims the stage
 *   (claimStage) and the shell's companion steps aside until it's gone.
 */

export type FencyReactionKind =
  | "love"
  | "success"
  | "error"
  | "celebrate"
  | "dizzy"
  | "wake";

export interface FencyLine {
  id: number;
  text: string;
  mood: FencyMood;
  priority: number;
  until: number;
}

export interface FencyReaction {
  id: number;
  kind: FencyReactionKind;
}

interface Bond {
  // ISO date (YYYY-MM-DD) of the first meeting, in local time.
  firstMet: string;
  lastDay: string;
  // Days with at least one visit.
  visits: number;
  pets: number;
  // Milestones (days together) already celebrated.
  milestones: number[];
}

type Translate = ReturnType<typeof useI18n>["t"];

const BOND_KEY = "fency-bond";
const MILESTONES = [7, 30, 100, 365];
// Line counts per locale list (fency.pet.N, ...): keep every locale in step.
const LINES = { pet: 6, wake: 3, success: 3, error: 2 } as const;
const SLEEP_AFTER_MS = 90_000;
const PAGE_HINT_DELAY_MS = 700;

// Route prefix → fency.pages.<key>, most specific first. Not the home
// page: FencyHello speaks there.
const PAGE_HINTS: [string, string][] = [
  ["/protection/assistant", "assistant"],
  ["/protection/secure-browser", "secureBrowser"],
  ["/protection/policies", "policies"],
  ["/protection/real-time-protection-events", "events"],
  ["/protection/security-policy-violation-events", "events"],
  ["/e-learning/courses", "courses"],
  ["/e-learning/management", "management"],
  ["/phishing", "campaigns"],
  ["/organization/user-risk", "userRisk"],
  ["/organization/users", "users"],
  ["/organization/groups", "groups"],
  ["/organization/subscription", "subscription"],
  ["/organization/configuration", "configuration"],
  ["/account", "account"],
];

// ── shared state ───────────────────────────────────────────────────────────
const line = ref<FencyLine | null>(null);
const reaction = ref<FencyReaction | null>(null);
const asleep = ref(false);
// A short-lived expression (a reaction), over the line's and the base mood.
const flash = ref<{ mood: FencyMood; until: number } | null>(null);
// The page's own feeling about things (e.g. the home's security posture).
const baseMood = ref<FencyMood>("idle");
const bond = ref<Bond | null>(null);
const now = ref(Date.now());
// Fencys shown by the page (not the companion) right now.
const onStage = ref(0);

let t: Translate | null = null;
let nextId = 1;
let userKey: string | null = null;
let lineTimer = 0;
let flashTimer = 0;
let idleTimer = 0;
let lastActivity = Date.now();
let hiddenAt = 0;
let started = 0;
let greeted = false;
const hintedPages = new Set<string>();
let pokes: number[] = [];

const pick = (list: keyof typeof LINES) =>
  t ? t(`fency.${list}.${Math.floor(Math.random() * LINES[list])}`) : "";

const localDay = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const daysBetween = (from: string, to: string) =>
  Math.round(
    (new Date(`${to}T12:00:00`).getTime() -
      new Date(`${from}T12:00:00`).getTime()) /
      86_400_000,
  );

const loadBond = (key: string): Bond | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Bond>;
    if (typeof parsed?.firstMet !== "string") return null;
    return {
      firstMet: parsed.firstMet,
      lastDay: parsed.lastDay ?? parsed.firstMet,
      visits: parsed.visits ?? 1,
      pets: parsed.pets ?? 0,
      milestones: Array.isArray(parsed.milestones) ? parsed.milestones : [],
    };
  } catch {
    return null;
  }
};

const saveBond = () => {
  if (!userKey || !bond.value) return;
  try {
    localStorage.setItem(userKey, JSON.stringify(bond.value));
  } catch {
    // Storage disabled or full: Fency just forgets a little.
  }
};

const daysTogether = computed(() =>
  bond.value ? daysBetween(bond.value.firstMet, localDay(new Date(now.value))) + 1 : 1,
);

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── speaking and reacting ──────────────────────────────────────────────────

function say(
  text: string,
  options: { mood?: FencyMood; priority?: number; duration?: number } = {},
) {
  if (!text) return;
  const priority = options.priority ?? 1;
  const current = line.value;
  if (current && current.until > Date.now() && current.priority > priority) {
    return;
  }
  // Longer lines stay longer: about a reading pace, within bounds.
  const duration =
    options.duration ?? Math.min(12_000, Math.max(5_000, text.length * 70));
  line.value = {
    id: nextId++,
    text,
    mood: options.mood ?? "idle",
    priority,
    until: Date.now() + duration,
  };
  window.clearTimeout(lineTimer);
  lineTimer = window.setTimeout(() => {
    line.value = null;
  }, duration);
}

function hush() {
  window.clearTimeout(lineTimer);
  line.value = null;
}

function react(kind: FencyReactionKind) {
  reaction.value = { id: nextId++, kind };
  const mood: FencyMood | null =
    kind === "error"
      ? "alert"
      : kind === "wake" || kind === "dizzy"
        ? null
        : "happy";
  if (!mood) return;
  flash.value = { mood, until: Date.now() + 1600 };
  window.clearTimeout(flashTimer);
  flashTimer = window.setTimeout(() => (flash.value = null), 1600);
}

/** A success or error the user just saw (e.g. a toast): Fency feels it too. */
function feel(kind: "success" | "error") {
  if (!t) return;
  react(kind);
  // Words only now and then: the toast already says what happened.
  if (kind === "error" || Math.random() < 0.35) {
    say(pick(kind), { mood: kind === "error" ? "idle" : "happy", priority: 2 });
  }
}

// ── the bond ──────────────────────────────────────────────────────────────

function pet() {
  wake();
  const at = Date.now();
  pokes = [...pokes.filter((p) => at - p < 4000), at];
  if (bond.value) {
    bond.value.pets += 1;
    saveBond();
  }
  if (pokes.length >= 6) {
    pokes = [];
    react("dizzy");
    say(t?.("fency.dizzy") ?? "", { priority: 3, duration: 4000 });
    return;
  }
  react("love");
  say(pick("pet"), { mood: "happy", priority: 2, duration: 4500 });
}

function timeOfDay(date = new Date()) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 18) return "afternoon";
  if (h >= 18 && h < 22) return "evening";
  return "night";
}

/**
 * Called once per session with the signed-in user: remembers the visit and
 * greets them the way an old friend would.
 */
function meet(user: { id: string; firstName?: string | null }) {
  if (!t) return;
  const key = `${BOND_KEY}:${user.id}`;
  if (userKey === key && greeted) return;
  userKey = key;

  const today = localDay();
  const known = loadBond(key);
  let hello: string;
  if (!known) {
    bond.value = {
      firstMet: today,
      lastDay: today,
      visits: 1,
      pets: 0,
      milestones: [],
    };
    hello = t("fency.hello.first");
  } else {
    const away = daysBetween(known.lastDay, today);
    bond.value = {
      ...known,
      lastDay: today,
      visits: known.visits + (away > 0 ? 1 : 0),
    };
    if (away <= 0)
      hello = t(timeOfDay() === "night" ? "fency.hello.night" : "fency.hello.today");
    else if (away < 4) hello = t("fency.hello.back");
    else hello = t("fency.hello.missed", { days: away });
  }
  saveBond();
  greeted = true;

  const name = user.firstName?.trim();
  const greeting = t(
    `fency.greet.${timeOfDay()}${name ? "" : "Anon"}`,
    name ? { name } : {},
  );
  say(`${greeting} ${hello}`, { mood: "happy", priority: 3, duration: 9000 });
  react("wake");

  // A milestone reached today: celebrate it once, after the greeting.
  const reached = MILESTONES.filter(
    (m) => daysTogether.value >= m && !bond.value!.milestones.includes(m),
  );
  if (reached.length && known) {
    const milestone = reached[reached.length - 1];
    bond.value.milestones = [...bond.value.milestones, ...reached];
    saveBond();
    window.setTimeout(() => {
      react("celebrate");
      say(t!(`fency.milestone.d${milestone}`), {
        mood: "happy",
        priority: 4,
        duration: 9000,
      });
    }, 9500);
  } else if (reached.length) {
    // A first meeting never "reaches" a milestone retroactively.
    bond.value.milestones = [...bond.value.milestones, ...reached];
    saveBond();
  }
}

/** A page opened: the first time this session, Fency says what it's about. */
function notePage(path: string) {
  if (!t) return;
  const hint = PAGE_HINTS.find(([prefix]) => path.startsWith(prefix));
  if (!hint || hintedPages.has(hint[1])) return;
  hintedPages.add(hint[1]);
  const key = hint[1];
  window.setTimeout(
    () => say(t!(`fency.pages.${key}`), { mood: "idle", priority: 0 }),
    PAGE_HINT_DELAY_MS,
  );
}

// ── sleep ─────────────────────────────────────────────────────────────────

function wake() {
  lastActivity = Date.now();
  if (!asleep.value) return;
  asleep.value = false;
  react("wake");
  say(pick("wake"), { mood: "happy", priority: 1, duration: 4000 });
}

const onActivity = () => wake();

const onVisibility = () => {
  if (document.hidden) {
    hiddenAt = Date.now();
    return;
  }
  // Back after a long while in another tab: a quiet "welcome back".
  if (hiddenAt && Date.now() - hiddenAt > 20 * 60_000 && t) {
    react("wake");
    say(t("fency.hello.back"), { mood: "happy", priority: 1 });
  }
  hiddenAt = 0;
  lastActivity = Date.now();
};

const tick = () => {
  now.value = Date.now();
  if (
    !asleep.value &&
    !document.hidden &&
    Date.now() - lastActivity > SLEEP_AFTER_MS
  ) {
    asleep.value = true;
    hush();
  }
};

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];

/** Brings Fency to life in the shell; the layout calls it once (refcounted). */
function start() {
  started += 1;
  if (started > 1) return;
  lastActivity = Date.now();
  ACTIVITY_EVENTS.forEach((type) =>
    window.addEventListener(type, onActivity, { passive: true, capture: true }),
  );
  // Moving the pointer counts as being around too.
  window.addEventListener("pointermove", onActivity, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  idleTimer = window.setInterval(tick, 5000);
}

function stop() {
  started = Math.max(0, started - 1);
  if (started > 0) return;
  ACTIVITY_EVENTS.forEach((type) =>
    window.removeEventListener(type, onActivity, { capture: true }),
  );
  window.removeEventListener("pointermove", onActivity);
  document.removeEventListener("visibilitychange", onVisibility);
  window.clearInterval(idleTimer);
  hush();
  asleep.value = false;
  greeted = false;
  hintedPages.clear();
}

/** A page Fency appeared: the companion hides. Call the result when it goes. */
export function claimStage() {
  onStage.value += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    onStage.value = Math.max(0, onStage.value - 1);
  };
}

/** How Fency looks right now, everything considered. */
const mood = computed<FencyMood>(() => {
  if (asleep.value) return "sleepy";
  if (flash.value) return flash.value.mood;
  if (line.value) return line.value.mood;
  return baseMood.value;
});

export function useFency() {
  // The global composer's `t`: it keeps working outside a setup function
  // (timers, listeners) and follows the active locale.
  if (!t) t = useI18n({ useScope: "global" }).t;

  return {
    line: readonly(line),
    reaction: readonly(reaction),
    asleep: readonly(asleep),
    mood,
    baseMood,
    bond: readonly(bond),
    daysTogether,
    onStage: computed(() => onStage.value > 0),
    reduceMotion,
    timeOfDay,
    say,
    hush,
    react,
    feel,
    pet,
    meet,
    notePage,
    wake,
    start,
    stop,
  };
}
